import { useEffect } from 'react';
import Hero from './components/Hero';
import HowItWorks from './components/HowItWorks';
import Pricing from './components/Pricing';
import Comparison from './components/Comparison';
import WeatherProtection from './components/WeatherProtection';
import EcoFriendly from './components/EcoFriendly';
import Testimonials from './components/Testimonials';
import ServiceArea from './components/ServiceArea';
import FAQ from './components/FAQ';
import BookingForm from './components/BookingForm';
import FinalCTA from './components/FinalCTA';
import Footer from './components/Footer';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const schemas = [
  {
    id: 'schema-local-business',
    data: {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      name: 'BoxFreeMove',
      description:
        'Reusable plastic moving tote rental service serving Grand Rapids and West Michigan. We deliver durable moving boxes to your door and pick them up when you\'re done.',
      url: siteUrl,
      telephone: '+16163897507',
      email: 'info@boxfreemove.com',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Grand Rapids',
        addressRegion: 'MI',
        addressCountry: 'US',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 42.9634,
        longitude: -85.6681,
      },
      areaServed: [
        { '@type': 'City', name: 'Grand Rapids', sameAs: 'https://en.wikipedia.org/wiki/Grand_Rapids,_Michigan' },
        { '@type': 'City', name: 'Kentwood' },
        { '@type': 'City', name: 'Wyoming' },
        { '@type': 'City', name: 'Walker' },
        { '@type': 'City', name: 'Grandville' },
        { '@type': 'City', name: 'Jenison' },
        { '@type': 'City', name: 'Hudsonville' },
        { '@type': 'City', name: 'Allendale' },
        { '@type': 'City', name: 'Rockford' },
        { '@type': 'City', name: 'Lowell' },
        { '@type': 'City', name: 'Ada' },
        { '@type': 'City', name: 'Cascade' },
        { '@type': 'City', name: 'Byron Center' },
        { '@type': 'City', name: 'Caledonia' },
        { '@type': 'City', name: 'Comstock Park' },
        { '@type': 'AdministrativeArea', name: 'West Michigan' },
        { '@type': 'AdministrativeArea', name: 'Kent County, Michigan' },
      ],
      serviceType: ['Moving Box Rental', 'Plastic Tote Rental', 'Moving Supplies Delivery'],
      priceRange: '$$',
      sameAs: [
        'https://www.facebook.com/boxfreemove',
        'https://www.instagram.com/boxfreemove',
      ],
    },
  },
  {
    id: 'schema-faq',
    data: {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'Do I have to clean the totes?',
          acceptedAnswer: { '@type': 'Answer', text: 'No. We sanitize all totes between rentals.' },
        },
        {
          '@type': 'Question',
          name: 'How far in advance should I reserve moving totes in Grand Rapids?',
          acceptedAnswer: { '@type': 'Answer', text: 'We recommend reserving at least one week before your move.' },
        },
        {
          '@type': 'Question',
          name: 'What areas of West Michigan does BoxFreeMove serve?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'BoxFreeMove delivers reusable moving totes throughout the Greater Grand Rapids area including Kentwood, Wyoming, Walker, Grandville, Jenison, Hudsonville, Allendale, Rockford, Lowell, Ada, Cascade, Byron Center, Caledonia, and Comstock Park.',
          },
        },
        {
          '@type': 'Question',
          name: 'What if I need more totes?',
          acceptedAnswer: { '@type': 'Answer', text: 'You can add extra totes to your order anytime.' },
        },
        {
          '@type': 'Question',
          name: 'What if I need the moving totes longer than a week?',
          acceptedAnswer: { '@type': 'Answer', text: 'Additional weeks are available for a small fee.' },
        },
        {
          '@type': 'Question',
          name: 'What if I break or lose a tote?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'No big deal — stuff happens! If you happen to damage or lose more than 2 totes during your move, we\'ll just ask you to cover the cost of replacement. Beyond that, you\'re covered.',
          },
        },
      ],
    },
  },
  {
    id: 'schema-service',
    data: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Moving Box Rental Grand Rapids',
      provider: {
        '@type': 'LocalBusiness',
        name: 'BoxFreeMove',
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Grand Rapids',
          addressRegion: 'MI',
          addressCountry: 'US',
        },
      },
      areaServed: 'West Michigan',
      description:
        'Reusable plastic moving tote rental with delivery and pickup included. Serving Grand Rapids, Kentwood, Wyoming, Walker, Rockford, and all of West Michigan.',
      offers: [
        {
          '@type': 'Offer',
          name: 'Apartment Move Package',
          description: '14 reusable moving totes, 7-day rental, delivery and pickup included. Best for studio or 1 bedroom moves in Grand Rapids.',
          price: '115',
          priceCurrency: 'USD',
        },
        {
          '@type': 'Offer',
          name: 'Standard Home Move Package',
          description: '28 reusable moving totes, 7-day rental, delivery and pickup included. Perfect for most 2–3 bedroom homes in West Michigan.',
          price: '160',
          priceCurrency: 'USD',
        },
        {
          '@type': 'Offer',
          name: 'Large Home Move Package',
          description: '38 reusable moving totes, 7-day rental, delivery and pickup included. Ideal for large homes in the Grand Rapids area.',
          price: '219',
          priceCurrency: 'USD',
        },
        {
          '@type': 'Offer',
          name: 'Estate Move Package',
          description: '50 reusable moving totes, 7-day rental, delivery and pickup included. Best for very large homes or downsizing in West Michigan.',
          price: '300',
          priceCurrency: 'USD',
        },
      ],
    },
  },
];

export default function HomePage() {
  useEffect(() => {
    schemas.forEach(({ id, data }) => {
      let el = document.getElementById(id);
      if (!el) {
        el = document.createElement('script');
        el.id = id;
        (el as HTMLScriptElement).type = 'application/ld+json';
        document.head.appendChild(el);
      }
      el.textContent = JSON.stringify(data);
    });

    return () => {
      schemas.forEach(({ id }) => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, []);

  const scrollToBooking = () => {
    const bookingSection = document.getElementById('booking-form');
    if (bookingSection) {
      bookingSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Hero onReserveClick={scrollToBooking} />
      <HowItWorks />
      <Pricing onReserveClick={scrollToBooking} />
      <Comparison />
      <WeatherProtection />
      <EcoFriendly />
      <Testimonials />
      <ServiceArea />
      <FAQ />
      <BookingForm />
      <FinalCTA onReserveClick={scrollToBooking} />
      <Footer />
    </div>
  );
}
